import React from 'react'
import { useNavigate } from 'react-router-dom'

import PassportBadge from '../PassportBadge'
import ProgressBar from '../ProgressBar'
import FocusableCard from './FocusableCard'

import { getFormattedOrdinals, secondsToHms, getTTSDuration } from '../../../../shared/helpers'
import type { Video } from '../../reducers/types/reducerTypes'
import type { CardProps } from './types'

export function VideoCard({
  data,
  index,
  onFocus,
  layout,
  ttsCounterText,
  onArrowPress,
  hideMetadata = true,
  focusKey,
  readShowTitle = true,
}: CardProps<Video>) {
  const navigate = useNavigate()

  const bgImage =
    data.images['asset-mezzanine-16x9'] ||
    data.images['asset-kids-mezzanine1-16x9'] ||
    data.images['asset-original'] ||
    ''
  const episodeTitle = data.title
  let showTitle
  if (data.parent.season) {
    showTitle = data.parent.season.show.title
  } else {
    showTitle = data.parent.show?.title
  }

  const handleOnEnterPress = () => {
    navigate(
      `/video-details/${encodeURIComponent(data.url)}?availability=${
        data.availability
      }`
    )
  }

  let metaData = ''
  let subText = ''
  if (!hideMetadata) {
    const formattedOrdinals = getFormattedOrdinals(data, true)
    const formattedOrdinalsTTS = getFormattedOrdinals(data)
    const durationFormatted = secondsToHms(data.duration)
    const durationTTS = data.duration ? getTTSDuration(data.duration) : ''
    metaData = durationFormatted
      ? `${formattedOrdinals} | ${durationFormatted}`
      : formattedOrdinals
    const metaDataTTSStr = durationTTS
      ? `${formattedOrdinalsTTS} ${durationTTS}`
      : formattedOrdinalsTTS
    subText = metaDataTTSStr ? `.... ${metaDataTTSStr}` : ''
  }

  const hasImage = !!bgImage

  const showTitleTTS = readShowTitle && showTitle ? `.... ${showTitle}` : ''
  const episodeTitleTTS = episodeTitle ? `.... ${episodeTitle}` : ''
  const ttsText = `${ttsCounterText} ${showTitleTTS} ${episodeTitleTTS}${subText}`
  return (
    <FocusableCard<Video>
      index={index}
      onFocus={onFocus}
      layout={layout}
      ttsText={ttsText}
      onEnterPress={handleOnEnterPress}
      onArrowPress={onArrowPress}
      focusKey={focusKey}
    >
      <div className="video-card">
        {data.flags.is_mvod && (
          <PassportBadge sizePx={120} className="video-card__passport-badge" />
        )}
        {hasImage && (
          <img
            className="video-card__background-image"
            src={`${bgImage}?resize=487x274&format=webp`}
            alt={`${showTitle} show poster`}
          />
        )}
        {!hasImage && <span className="video-card__title">{data.title}</span>}
        {data.seconds_watched ? (
          <ProgressBar current={data.seconds_watched} total={data.duration} />
        ) : null}
      </div>
      {hideMetadata ? (
        <>
          <h3 className="video-card__show-title">{showTitle}</h3>
          <h3 className="video-card__episode-title">{episodeTitle}</h3>
        </>
      ) : (
        <>
          <h3 className="video-card__episode-title">{episodeTitle}</h3>
          {metaData && <h3 className="video-card__meta-title">{metaData}</h3>}
        </>
      )}
    </FocusableCard>
  )
}
